import {uploadFile as uploadFileToAssetStorage} from '@github-ui/comment-box/fileUpload'
import {useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import type {FileType} from '@github-ui/use-unified-file-select'
import {useCallback, useRef, useState, type RefObject} from 'react'

import {extractImageUrls} from '@github-ui/copilot-shared-ui/composer/markdown-images'

export const ACCEPTED_IMAGE_FILE_TYPES: FileType[] = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

/** Maximum number of images allowed in a single task prompt. */
export const MAX_TASK_IMAGES = 4

export type UseMarkdownImageUploadOptions = {
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  repositoryId?: string
}

export type UseMarkdownImageUploadReturn = {
  /** Process an iterable of files for upload and markdown insertion. */
  processFiles: (files: Iterable<File>) => void
  /** True when one or more uploads are in-flight. */
  isUploading: boolean
}

function makePlaceholder(filename: string): string {
  return `<!-- Uploading "${filename}"... -->`
}

function makeFailurePlaceholder(filename: string): string {
  return `<!-- Failed to upload "${filename}" -->`
}

/**
 * Determine how many newlines to insert before the placeholder so it doesn't
 * jam into existing text. Ported from @github-ui/markdown-editor's UseFileHandling.
 */
function getNewlinePrefix(input: string, selectionStart: number): string {
  if (selectionStart === 0) return ''

  if (input[selectionStart - 1] === '\n') {
    if (selectionStart === 1 || input[selectionStart - 2] === '\n') return ''
  }

  const previousNewlineIndex = input.lastIndexOf('\n', selectionStart - 1)
  if (/\S/.test(input.substring(previousNewlineIndex, selectionStart))) {
    return '\n\n'
  }

  return '\n'
}

/**
 * Determine how many newlines to insert after the placeholder so subsequent
 * text doesn't run into it. Ported from @github-ui/markdown-editor's UseFileHandling.
 */
function getNewlineSuffix(input: string, selectionEnd: number): string {
  if (input.length === selectionEnd) return ''

  if (input[selectionEnd] === '\n') {
    if (selectionEnd === input.length - 1 || input[selectionEnd + 1] === '\n') return ''
  }

  const nextNewlineIndex = input.indexOf('\n', selectionEnd)
  if (/\S/.test(input.substring(selectionEnd, nextNewlineIndex))) {
    return '\n\n'
  }

  return '\n'
}

/**
 * Upload a single image file using the comment-box uploadFile utility,
 * which correctly handles the /upload/policies/assets endpoint including
 * same_origin authenticity tokens and the optional asset_upload_url completion step.
 * Returns the asset URL on success.
 */
async function uploadImageFile(file: File, repositoryId: string | undefined): Promise<string> {
  const {url} = await uploadFileToAssetStorage(file, repositoryId)
  return url
}

/**
 * Core hook providing the image upload → markdown insertion lifecycle.
 *
 * Handles: placeholder insertion, async upload, and replacement with markdown
 * `![filename](url)` syntax. Manages concurrent upload counting so `isUploading`
 * remains `true` until all in-flight uploads finish.
 */
export function useMarkdownImageUpload({
  textAreaRef,
  repositoryId,
}: UseMarkdownImageUploadOptions): UseMarkdownImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false)
  const uploadCountRef = useRef(0)
  const emitChange = useSyntheticChange({
    inputRef: textAreaRef,
    fallbackEventHandler: () => {},
  })

  const safeReplaceAndFinish = useSafeAsyncCallback((placeholder: string, replacement: string) => {
    const textarea = textAreaRef.current

    if (textarea) {
      const index = textarea.value.indexOf(placeholder)
      if (index !== -1) {
        emitChange(replacement, [index, index + placeholder.length])
      }
    }

    // Always update bookkeeping regardless of textarea availability
    uploadCountRef.current -= 1
    setIsUploading(uploadCountRef.current > 0)
  })

  const processImageFile = useCallback(
    async (file: File) => {
      const textarea = textAreaRef.current
      if (!textarea) return

      const filename = file.name || 'image.png'

      // Insert placeholder with smart newline handling to avoid jamming into existing text
      const newlinePrefix = getNewlinePrefix(textarea.value, textarea.selectionStart)
      const newlineSuffix = getNewlineSuffix(textarea.value, textarea.selectionEnd)
      const placeholder = makePlaceholder(filename)
      emitChange(`${newlinePrefix}${placeholder}${newlineSuffix}`)
      uploadCountRef.current += 1
      setIsUploading(uploadCountRef.current > 0)

      try {
        const url = await uploadImageFile(file, repositoryId)
        const markdown = `![${filename}](${url})`
        safeReplaceAndFinish(placeholder, markdown)
      } catch {
        safeReplaceAndFinish(placeholder, makeFailurePlaceholder(filename))
      }
    },
    [textAreaRef, repositoryId, emitChange, safeReplaceAndFinish],
  )

  const processFiles = useCallback(
    (files: Iterable<File>) => {
      const textarea = textAreaRef.current
      const existingCount = textarea ? extractImageUrls(textarea.value).length : 0
      const inFlightCount = uploadCountRef.current
      const available = MAX_TASK_IMAGES - existingCount - inFlightCount

      if (available <= 0) {
        return
      }

      let accepted = 0
      for (const file of files) {
        // Only process files with accepted image MIME types
        if (!ACCEPTED_IMAGE_FILE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_FILE_TYPES)[number])) {
          continue
        }

        if (accepted >= available) {
          break
        }

        void processImageFile(file)
        accepted++
      }
    },
    [processImageFile, textAreaRef],
  )

  return {
    processFiles,
    isUploading,
  }
}
